import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ChevronLeft, ChevronRight, FileText, CalendarClock, Link2, SlidersHorizontal, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { resolveDisplayZone } from '../../lib/timezone';
import { errorText } from '../../lib/apiErrors';
import { buildBookingLinkLocationOptions } from '../schedule/locationOptions';
import {
    AUTO_CALENDAR, EDITOR_STEPS, newBookingLinkForm, bookingLinkFormFromExisting, validateBookingLinkForm,
    buildMemberOptions, oneOnOneAssignees,
} from './bookingLinkForm';
import DetailsSection from './sections/DetailsSection';
import DateTimeSection from './sections/DateTimeSection';
import IntegrationSection from './sections/IntegrationSection';
import OthersSection from './sections/OthersSection';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const STEP_ICONS = { details: FileText, schedule: CalendarClock, integration: Link2, others: SlidersHorizontal };

const PROVIDER_NAMES = { google: 'Google Calendar', outlook: 'Outlook Calendar' };

const bareLocationLabel = (options, value) =>
    (options.find(o => o.value === value)?.label || value).replace(/ \(auto-link\)$/, '');

const memberName = (rows, userId) => rows.find(r => r.user_id === userId)?.display_name || 'the first coach';

/**
 * Create/Edit Booking Link — #685 groups the seventeen fields into four steps
 * (Details · Date and Time · Integration · Others). The restructure is
 * presentation only: one `form` state, the same defaults, the same payload
 * (pinned by BookingLinks.editor.contract.test.jsx). #753 changed who can be
 * assigned — coach profiles from Admin → Coaches, with at least one required —
 * but still adds no form key. #790 gives a 1:1 link exactly one coach (a
 * dropdown, not the checklist), again with no new form key.
 *
 * Every step stays MOUNTED (`forceMount`, collapsed with `hidden`): existing
 * suites query fields on any step by data-testid, and a validation error on a
 * step the user is not looking at must be focusable once the editor opens it.
 * Save is available from every step.
 */
export function BookingLinkModal({ open, onClose, existing, getAuthHeader, onSaved }) {
    // #579: a new link's hours are authored in the OWNER's zone — profile
    // timezone first, browser only as the fallback. The value is persisted
    // and the server anchors the availability window to it.
    const { user } = useAuth();
    const ownerZone = resolveDisplayZone({ user });
    const [form, setForm] = useState(() => newBookingLinkForm(ownerZone));
    // #753: assignees are coach profiles (Admin → Coaches). /admin/users only
    // adds the calendar badge and names grandfathered non-coach assignees.
    const [coaches, setCoaches] = useState([]);
    const [orgUsers, setOrgUsers] = useState([]);
    const [usersLoaded, setUsersLoaded] = useState(false);
    // 'loading' | 'ready' | 'failed' — lets the assignment list tell "no
    // coaches" apart from still loading or a coach list that failed to load.
    const [membersStatus, setMembersStatus] = useState('loading');
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(EDITOR_STEPS[0].value);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [errors, setErrors] = useState([]);
    const [locationNotice, setLocationNotice] = useState('');
    // #790: which coach a 1:1 link kept, after a type switch or when a legacy
    // multi-coach link opened. DetailsSection shows it on 1:1 only.
    const [assigneeNotice, setAssigneeNotice] = useState('');
    const pendingFocus = useRef(null);
    const bodyRef = useRef(null);

    // Location options follow the hosting calendar (#514). When the calendar
    // changes, drop a location it cannot mint (Meet on Outlook, Teams on
    // Google) and fall back to the first option — mirrors the Schedule tab.
    // With `calendar: ''` (auto) either provider may end up hosting, so both
    // conference options stay on offer.
    const locationOptions = useMemo(() => buildBookingLinkLocationOptions(form.calendar), [form.calendar]);
    useEffect(() => {
        setForm((f) => (locationOptions.some((o) => o.value === f.location)
            ? f
            : { ...f, location: locationOptions[0].value }));
    }, [locationOptions]);

    useEffect(() => {
        if (open) {
            setForm(existing ? bookingLinkFormFromExisting(existing, ownerZone) : newBookingLinkForm(ownerZone));
            setStep(EDITOR_STEPS[0].value);
            setAdvancedOpen(false);
            setErrors([]);
            setLocationNotice('');
            setAssigneeNotice('');
            // Load the coaches (the assignable people) and the org users (the
            // calendar badge + names of grandfathered non-coach assignees). A
            // failed /admin/users only drops those extras; a failed coach list
            // fails the whole picker. `alive` ignores a response that lands
            // after the editor was closed or reopened.
            setMembersStatus('loading');
            let alive = true;
            Promise.allSettled([
                axios.get(`${API}/admin/coaches`, { headers: getAuthHeader() }),
                axios.get(`${API}/admin/users`, { headers: getAuthHeader() }),
            ]).then(([coachesRes, usersRes]) => {
                if (!alive) return;
                if (coachesRes.status !== 'fulfilled') {
                    setCoaches([]);
                    setOrgUsers([]);
                    setUsersLoaded(false);
                    setMembersStatus('failed');
                    return;
                }
                const coachList = Array.isArray(coachesRes.value.data) ? coachesRes.value.data : [];
                const usersOk = usersRes.status === 'fulfilled' && Array.isArray(usersRes.value.data);
                const userList = usersOk ? usersRes.value.data : [];
                setCoaches(coachList);
                setOrgUsers(userList);
                setUsersLoaded(usersOk);
                setMembersStatus('ready');
                // #790: a legacy 1:1 link holding several coaches opens on the
                // one its public page books (link_hosts uses members[:1]), and
                // saving stores just that one. Only here, with the coach list
                // in hand: after a failed load the coach in use cannot be told,
                // so nothing is reduced and the 1:1 rule blocks the save.
                const stored = existing?.assigned_users || [];
                if (existing && existing.booking_type !== 'round_robin' && stored.length > 1) {
                    const rows = buildMemberOptions(coachList, userList, stored, usersOk);
                    const [kept] = oneOnOneAssignees(stored, rows);
                    const keptRow = rows.find(r => r.user_id === kept.user_id);
                    setForm(f => (f.booking_type !== 'round_robin' && f.assigned_users.length > 1
                        ? { ...f, assigned_users: oneOnOneAssignees(f.assigned_users, buildMemberOptions(coachList, userList, f.assigned_users, usersOk)) }
                        : f));
                    setAssigneeNotice(
                        `This link listed ${stored.length} coaches, but a 1:1 link only ever books one. `
                        + `Kept ${memberName(rows, kept.user_id)}${keptRow?.eligible ? ', the coach it was using' : ''}; saving removes the others.`
                    );
                }
            });
            return () => { alive = false; };
        }
        return undefined;
    }, [open, existing, getAuthHeader, ownerZone]);

    // Derived in render so the rows follow every toggle and never read the
    // pre-open `form` a fetch callback would have closed over.
    const members = useMemo(
        () => buildMemberOptions(coaches, orgUsers, form.assigned_users, usersLoaded),
        [coaches, orgUsers, form.assigned_users, usersLoaded],
    );

    // The member-aware rules (#753 / A8.3) only run once the coach list is in;
    // a loading or failed list must not block a save on a guess.
    const validationMembers = membersStatus === 'ready' ? members : undefined;

    // After a failed submit, re-check as the user edits so a fixed field
    // clears its message; before the first failed submit nothing is shown.
    useEffect(() => {
        setErrors(prev => (prev.length ? validateBookingLinkForm(form, validationMembers) : prev));
    }, [form, validationMembers]);

    // Focus the first invalid field once its step (and Advanced) is showing.
    useEffect(() => {
        if (!pendingFocus.current) return;
        const el = document.getElementById(pendingFocus.current);
        pendingFocus.current = null;
        el?.focus();
    }, [step, advancedOpen, errors]);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [step]);

    // The fallback itself stays in the effect above, untouched. This only
    // explains it: when the new calendar cannot create the current location,
    // say what it becomes. Opening a link never triggers it.
    const changeCalendar = (value) => {
        const calendar = value === AUTO_CALENDAR ? '' : value;
        const nextOptions = buildBookingLinkLocationOptions(calendar);
        if (nextOptions.some(o => o.value === form.location)) {
            setLocationNotice('');
        } else {
            setLocationNotice(
                `${bareLocationLabel(locationOptions, form.location)} isn't available on ${PROVIDER_NAMES[calendar] || 'this calendar'} — switched to ${bareLocationLabel(nextOptions, nextOptions[0].value)}.`
            );
        }
        setForm({ ...form, calendar });
    };

    const changeLocation = (location) => {
        setLocationNotice('');
        setForm({ ...form, location });
    };

    // #790: a 1:1 link keeps one coach. Leaving round robin keeps the one the
    // link would book (first eligible in the current order) and says so; the
    // others are not restored on the way back. Before the coach list is in,
    // the list stays as it is and the 1:1 rule blocks a save instead.
    const changeType = (booking_type) => {
        if (booking_type !== 'round_robin' && form.assigned_users.length > 1 && membersStatus === 'ready') {
            const kept = oneOnOneAssignees(form.assigned_users, members);
            setAssigneeNotice(`A 1:1 link has one coach — kept ${memberName(members, kept[0].user_id)}.`);
            setForm({ ...form, booking_type, assigned_users: kept });
            return;
        }
        setAssigneeNotice('');
        setForm({ ...form, booking_type });
    };

    // #790: the 1:1 dropdown. A 1:1 link ignores weight, so it is stored as 1.
    const pickCoach = (user_id) => {
        setAssigneeNotice('');
        setForm({ ...form, assigned_users: [{ user_id, weight: 1 }] });
    };

    const handleSubmit = async () => {
        const found = validateBookingLinkForm(form, validationMembers);
        setErrors(found);
        if (found.length) {
            const first = found[0];
            setStep(first.step);
            if (first.advanced) setAdvancedOpen(true);
            pendingFocus.current = first.field;
            return toast.error(first.message);
        }
        setLoading(true);
        // '' is the editor's "auto" — send null so _validate_booking_calendar
        // records "infer at booking time" instead of persisting a guess the
        // assignee may not own (#514 audit A02).
        // #758: the guest coach picker is honoured only on round robin, so a
        // 1:1 link always saves it off (no surprise if it is later converted).
        const payload = {
            ...form,
            calendar: form.calendar || null,
            allow_guest_coach_choice: form.booking_type === 'round_robin' && form.allow_guest_coach_choice === true,
        };
        try {
            if (existing) {
                await axios.put(`${API}/booking/links/${existing.booking_link_id}`, payload, { headers: getAuthHeader() });
                toast.success('Booking link updated');
            } else {
                await axios.post(`${API}/booking/links`, payload, { headers: getAuthHeader() });
                toast.success('Booking link created');
            }
            onSaved();
            onClose();
        } catch (err) {
            // errorText: #753 adds a 403 whose `detail` is an object
            // ({error: "org_scope_required", message}) for an org-less admin.
            toast.error(errorText(err, 'Failed to save'));
        } finally {
            setLoading(false);
        }
    };

    const stepIndex = EDITOR_STEPS.findIndex(s => s.value === step);
    const isLastStep = stepIndex === EDITOR_STEPS.length - 1;
    const errorFor = (field) => errors.find(e => e.field === field)?.message;
    const stepHasError = (value) => errors.some(e => e.step === value);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            {/* A fixed height (capped by the viewport) keeps the footer — and Next
                under the pointer — still while stepping between tabs of
                different lengths; the body scrolls instead. */}
            <DialogContent className="sm:max-w-3xl h-[min(90vh,760px)] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="booking-link-modal">
                <Tabs value={step} onValueChange={setStep} className="flex flex-col flex-1 min-h-0">
                    <div className="shrink-0 px-6 pt-6 border-b border-stone-200">
                        <DialogHeader>
                            <DialogTitle>{existing ? 'Edit' : 'New'} Booking Link</DialogTitle>
                            <DialogDescription>Configure your scheduling page</DialogDescription>
                        </DialogHeader>
                        <div className="overflow-x-auto -mx-6 px-6">
                            <TabsList aria-label="Booking link sections" className="mt-4 h-auto bg-transparent p-0 gap-6 rounded-none justify-start">
                                {EDITOR_STEPS.map(s => {
                                    const Icon = STEP_ICONS[s.value];
                                    const hasError = stepHasError(s.value);
                                    return (
                                        <TabsTrigger key={s.value} value={s.value} data-testid={`bl-tab-${s.value}`}
                                            className="gap-1.5 rounded-none border-b-2 border-transparent px-0.5 pb-3 pt-0 text-stone-500 shadow-none data-[state=active]:border-violet-600 data-[state=active]:text-violet-700 data-[state=active]:shadow-none data-[state=active]:bg-transparent">
                                            <Icon className="w-4 h-4" aria-hidden="true" />
                                            {s.label}
                                            {hasError && (
                                                <>
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-hidden="true" data-testid={`bl-tab-error-${s.value}`} />
                                                    <span className="sr-only">(has errors)</span>
                                                </>
                                            )}
                                        </TabsTrigger>
                                    );
                                })}
                            </TabsList>
                        </div>
                    </div>

                    <div ref={bodyRef} className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
                        <TabsContent value="details" forceMount hidden={step !== 'details'} className="mt-0">
                            <DetailsSection form={form} setForm={setForm} members={members} membersStatus={membersStatus} errorFor={errorFor}
                                onTypeChange={changeType} onPickCoach={pickCoach} assigneeNotice={assigneeNotice} />
                        </TabsContent>
                        <TabsContent value="schedule" forceMount hidden={step !== 'schedule'} className="mt-0">
                            <DateTimeSection form={form} setForm={setForm} errorFor={errorFor}
                                advancedOpen={advancedOpen} setAdvancedOpen={setAdvancedOpen} />
                        </TabsContent>
                        <TabsContent value="integration" forceMount hidden={step !== 'integration'} className="mt-0">
                            <IntegrationSection form={form} locationOptions={locationOptions}
                                onCalendarChange={changeCalendar} onLocationChange={changeLocation}
                                locationNotice={locationNotice} />
                        </TabsContent>
                        <TabsContent value="others" forceMount hidden={step !== 'others'} className="mt-0">
                            <OthersSection form={form} setForm={setForm} />
                        </TabsContent>
                    </div>
                </Tabs>

                {/* Footer per the #685 mockup: Cancel · Back (from step 2) · Next
                    (until the last step) · Create/Save — save is available on every
                    step. Narrow screens: the step caption hides and Create takes its
                    own full-width row, so the buttons never overflow the dialog. */}
                <DialogFooter className="shrink-0 flex-row items-center justify-between sm:justify-between gap-3 border-t border-stone-200 bg-stone-50/60 px-6 py-4 sm:space-x-0">
                    <p className="hidden sm:block text-xs text-stone-500 truncate" aria-live="polite" data-testid="bl-step-indicator">
                        Step {stepIndex + 1} of {EDITOR_STEPS.length} · {EDITOR_STEPS[stepIndex].label}
                    </p>
                    <div className="flex flex-wrap items-center justify-end gap-2 w-full sm:w-auto sm:shrink-0">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        {stepIndex > 0 && (
                            <Button type="button" variant="outline" onClick={() => setStep(EDITOR_STEPS[stepIndex - 1].value)} data-testid="bl-back">
                                <ChevronLeft className="w-4 h-4 mr-1" aria-hidden="true" /> Back
                            </Button>
                        )}
                        {!isLastStep && (
                            <Button type="button" onClick={() => setStep(EDITOR_STEPS[stepIndex + 1].value)}
                                className="bg-stone-900 text-white hover:bg-stone-800" data-testid="bl-next">
                                Next <ChevronRight className="w-4 h-4 ml-1" aria-hidden="true" />
                            </Button>
                        )}
                        <Button type="button" onClick={handleSubmit} disabled={loading} className="w-full sm:w-auto bg-violet-600 text-white hover:bg-violet-700" data-testid="bl-submit">
                            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" aria-hidden="true" />}
                            {existing ? 'Save Changes' : 'Create Booking Link'}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
